// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useSearchParams } from 'react-router-dom';

import { api } from '../../../api/client';
import type { PanoramaInfo } from '../../../api/types';
import { useAsync } from '../../../app/useAsync';
import { describePlacementDa, resolvePlacement } from '../../../viewport/panorama';
import styles from './ModelSettings.module.css';

/**
 * Lag › Gennemse panoramaer: every 360° panorama in the case as a row
 * (thumbnail, file, placement). A row is a place to stand, not a toggle:
 * it enters the panorama (`?pano=`, which the Ground absorbs); the active
 * one leaves it. One the case cannot place is listed and disabled, so
 * "this exists but nothing knows where it was taken" is visible. The old
 * viewport's PanoramaPanel, in the gear panel.
 */
export function PanoramaList() {
  const [params, setParams] = useSearchParams();
  const read = useAsync<PanoramaInfo[]>((signal) => api.panoramas(signal), []);
  const active = params.get('pano');
  const enter = (id: number | null) => {
    const next = new URLSearchParams(params);
    if (id === null) next.delete('pano');
    else next.set('pano', String(id));
    setParams(next, { replace: true });
  };

  if (read.error) return <p className={styles.note}>Panoramaerne kunne ikke hentes: {read.error.message}</p>;
  if (!read.data) return <p className={styles.note}>Henter panoramaer…</p>;
  if (read.data.length === 0) return <p className={styles.note}>Ingen 360°-panoramaer i sagen.</p>;
  return (
    <ul className={styles.panos} aria-label="Panoramaer">
      {read.data.map((pano) => {
        const placement = resolvePlacement(pano);
        const on = active === String(pano.id);
        return (
          <li key={pano.id}>
            <button
              type="button"
              className={`${styles.pano}${on ? ` ${styles.panoOn}` : ''}`}
              disabled={placement === null}
              aria-pressed={on}
              onClick={() => enter(on ? null : pano.id)}
              title={on ? 'Forlad panoramaet' : placement ? 'Stå i panoramaet' : undefined}
            >
              <img className={styles.panoThumb} src={api.panoramaImageUrl(pano.id, { maxSize: 128 })} alt="" loading="lazy" />
              <span className={styles.panoText}>
                <span className={styles.panoName} title={pano.filename}>
                  {pano.filename}
                </span>
                <span className={styles.note}>{describePlacementDa(pano, placement)}</span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
